import { useState, useEffect, useMemo } from 'react'
import { X, ExternalLink, ArrowUpDown } from 'lucide-react'
import { presalesDashboardApi } from '@/api/client'
import { formatCurrency } from '../utils/dealHelpers'
import type { KR1Deal } from '@/api/types'

interface KR1DealDetailProps {
  isOpen: boolean
  onClose: () => void
}

type SortColumn = 'deal_name' | 'presales_engineer' | 'presales_engagement_date' | 'close_date' | 'amount' | 'status'

export function KR1DealDetailModal({ isOpen, onClose }: KR1DealDetailProps) {
  const [deals, setDeals] = useState<KR1Deal[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [sortBy, setSortBy] = useState<SortColumn>('presales_engagement_date')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc')
  const [statusFilter, setStatusFilter] = useState<'all' | 'won' | 'lost' | 'open'>('all')

  useEffect(() => {
    if (isOpen) {
      setIsLoading(true)
      setError(null)
      presalesDashboardApi.getKR1Deals({ period: 'all_time' })
        .then(res => {
          if (res.success && res.data) {
            setDeals(res.data.deals)
          } else {
            setError(res.error || 'Failed to load deal data')
          }
        })
        .catch(err => setError(err.message || 'Failed to load deal data'))
        .finally(() => setIsLoading(false))
    }
  }, [isOpen])

  const filteredDeals = useMemo(() => {
    if (statusFilter === 'all') return deals
    return deals.filter(d => d.status === statusFilter)
  }, [deals, statusFilter])

  const sortedDeals = useMemo(() => {
    return [...filteredDeals].sort((a, b) => {
      let cmp = 0
      switch (sortBy) {
        case 'deal_name':
          cmp = (a.deal_name || '').localeCompare(b.deal_name || '')
          break
        case 'presales_engineer':
          cmp = (a.presales_engineer || '').localeCompare(b.presales_engineer || '')
          break
        case 'presales_engagement_date':
          cmp = (a.presales_engagement_date || '').localeCompare(b.presales_engagement_date || '')
          break
        case 'close_date':
          cmp = (a.close_date || '').localeCompare(b.close_date || '')
          break
        case 'amount':
          cmp = (a.amount ?? 0) - (b.amount ?? 0)
          break
        case 'status': {
          const order = { won: 0, lost: 1, open: 2 }
          cmp = order[a.status] - order[b.status]
          break
        }
      }
      return sortDir === 'desc' ? -cmp : cmp
    })
  }, [filteredDeals, sortBy, sortDir])

  const handleSort = (col: SortColumn) => {
    if (sortBy === col) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    } else {
      setSortBy(col)
      setSortDir('asc')
    }
  }

  const wonCount = deals.filter(d => d.status === 'won').length
  const lostCount = deals.filter(d => d.status === 'lost').length
  const openCount = deals.filter(d => d.status === 'open').length

  if (!isOpen) return null

  const SortHeader = ({ column, children, className = '' }: { column: SortColumn; children: React.ReactNode; className?: string }) => (
    <th
      className={`pb-2 pr-3 cursor-pointer select-none hover:text-[var(--text-secondary)] transition-colors ${className}`}
      onClick={() => handleSort(column)}
    >
      <span className="inline-flex items-center gap-1">
        {children}
        {sortBy === column ? (
          <span className="text-primary">{sortDir === 'asc' ? '↑' : '↓'}</span>
        ) : (
          <ArrowUpDown className="h-3 w-3 opacity-30" />
        )}
      </span>
    </th>
  )

  const formatDate = (d: string | null) => {
    if (!d) return '-'
    return new Date(d).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: '2-digit' })
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" onClick={onClose}>
      <div className="absolute inset-0 bg-black/60" />
      <div className="relative z-10 w-full max-w-5xl mx-4 max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-xl shadow-2xl flex flex-col max-h-[85vh]">
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-3 border-b border-[var(--border-color)] flex-shrink-0">
            <div>
              <h3 className="text-sm font-semibold text-[var(--text-primary)]">KR1 — Win Ratio Deal Breakdown</h3>
              <p className="text-xs text-[var(--text-muted)] mt-0.5">
                Strategic deals with presales engagement (all time)
              </p>
            </div>
            <button onClick={onClose} className="p-1 rounded hover:bg-[var(--bg-primary)] text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors">
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Filter pills */}
          <div className="px-5 py-3 border-b border-[var(--border-color)] flex items-center gap-2 flex-shrink-0">
            {[
              { key: 'all' as const, label: `All (${deals.length})` },
              { key: 'won' as const, label: `Won (${wonCount})`, color: 'text-green-400' },
              { key: 'lost' as const, label: `Lost (${lostCount})`, color: 'text-red-400' },
              { key: 'open' as const, label: `Open (${openCount})`, color: 'text-[var(--text-muted)]' },
            ].map(({ key, label, color }) => (
              <button
                key={key}
                onClick={() => setStatusFilter(key)}
                className={`px-2.5 py-1 text-xs rounded-full border transition-colors ${
                  statusFilter === key
                    ? 'bg-primary/10 border-primary/40 text-primary'
                    : `border-[var(--border-color)] ${color || 'text-[var(--text-secondary)]'} hover:border-[var(--text-muted)]`
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Content */}
          <div className="overflow-auto flex-1 px-5 py-3">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
              </div>
            ) : error ? (
              <div className="text-center py-12 text-sm text-red-400">{error}</div>
            ) : sortedDeals.length === 0 ? (
              <div className="text-center py-12 text-sm text-[var(--text-muted)]">No deals found.</div>
            ) : (
              <table className="w-full text-sm">
                <thead className="sticky top-0 z-10 bg-[var(--bg-secondary)]">
                  <tr className="text-left text-xs font-medium text-[var(--text-muted)] uppercase tracking-wider border-b border-[var(--border-color)]">
                    <SortHeader column="deal_name">Deal</SortHeader>
                    <SortHeader column="presales_engineer">Engineer</SortHeader>
                    <SortHeader column="presales_engagement_date">Engaged</SortHeader>
                    <SortHeader column="close_date">Closed</SortHeader>
                    <SortHeader column="amount" className="text-right">Amount</SortHeader>
                    <SortHeader column="status">Status</SortHeader>
                    <th className="pb-2 w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {sortedDeals.map(deal => (
                    <tr key={deal.hubspot_deal_id} className="border-b border-[var(--border-color)] hover:bg-[var(--bg-primary)] transition-colors">
                      <td className="py-2 pr-3 max-w-[250px]">
                        <div className="truncate font-medium text-[var(--text-primary)]">{deal.deal_name}</div>
                        <div className="text-xs text-[var(--text-muted)]">{deal.dealtype}</div>
                      </td>
                      <td className="py-2 pr-3 text-[var(--text-muted)]">{deal.presales_engineer || deal.solutions_consultant_owner || '-'}</td>
                      <td className="py-2 pr-3 text-[var(--text-muted)]">{formatDate(deal.presales_engagement_date)}</td>
                      <td className="py-2 pr-3 text-[var(--text-muted)]">{deal.is_closed ? formatDate(deal.close_date) : '-'}</td>
                      <td className="py-2 pr-3 text-right text-[var(--text-muted)]">{formatCurrency(deal.amount)}</td>
                      <td className="py-2 pr-3">
                        <span className={`inline-block px-1.5 py-0.5 text-[10px] font-medium rounded ${
                          deal.status === 'won' ? 'bg-green-900/30 text-green-300 border border-green-700/40' :
                          deal.status === 'lost' ? 'bg-red-900/30 text-red-300 border border-red-700/40' :
                          'bg-blue-900/30 text-blue-300 border border-blue-700/40'
                        }`}>
                          {deal.status === 'won' ? 'Won' : deal.status === 'lost' ? 'Lost' : 'Open'}
                        </span>
                      </td>
                      <td className="py-2">
                        <a
                          href={`https://app.hubspot.com/contacts/22499506/deal/${deal.hubspot_deal_id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[var(--text-muted)] hover:text-primary transition-colors"
                          title="Open in HubSpot"
                        >
                          <ExternalLink className="h-3.5 w-3.5" />
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
